import { useEffect, useRef, useState, type FormEvent } from 'react';
import { FileText, Lock, Mic, Paperclip, Send, Trash2, X } from 'lucide-react';

export interface ChatMessage { id: string; kind: 'client' | 'staff' | 'system' | string; body: string; internal?: boolean; createdAt: string; from: string | null; file: { id: string; name: string | null; mime?: string | null } | null }

const time = (iso: string) => new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** A file in the chat: images preview, other files open in a new tab. Files are fetched with the signed-in token. */
function Attachment({ id, name, mime, load }: { id: string; name: string | null; mime?: string | null; load: (id: string) => Promise<string> }) {
  const [url, setUrl] = useState<string | null>(null);
  const [type, setType] = useState('');
  useEffect(() => {
    let u = '';
    load(id).then(async (x) => { u = x; const b = await fetch(x).then((r) => r.blob()); setType(b.type); setUrl(x); }).catch(() => undefined);
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [id]);
  if (!url) return <span className="chat-file">{mime?.startsWith('audio/') ? <Mic /> : <FileText />}{mime?.startsWith('audio/') ? 'Voice note' : name ?? 'File'}</span>;
  if (type.startsWith('audio/') || mime?.startsWith('audio/')) return <span className="chat-voice"><Mic /><audio controls preload="metadata" src={url} aria-label={name ?? 'Voice note'} />{name && /\d:\d\d/.test(name) && <small>{/\d+:\d\d/.exec(name)![0]}</small>}</span>;
  if (type.startsWith('image/')) return <a href={url} target="_blank" rel="noreferrer" className="chat-img"><img src={url} alt={name ?? 'Attachment'} /></a>;
  return <a href={url} target="_blank" rel="noreferrer" download={name ?? undefined} className="chat-file"><FileText />{name ?? 'File'}</a>;
}

const VOICE_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
const MAX_VOICE = 300; // seconds
const clock = (n: number) => `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;

/** Records a voice note with the microphone. Stops on its own at five minutes. */
function useVoiceNote() {
  const [state, setState] = useState<'idle' | 'rec'>('idle');
  const [secs, setSecs] = useState(0);
  const [error, setError] = useState('');
  const rec = useRef<{ mr: MediaRecorder; chunks: Blob[]; stream: MediaStream; timer: number; started: number; done?: (f: File | null) => void } | null>(null);
  const supported = typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  async function start() {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const type = VOICE_TYPES.find((t) => MediaRecorder.isTypeSupported(t));
      const mr = new MediaRecorder(stream, type ? { mimeType: type, audioBitsPerSecond: 32000 } : undefined);
      const r = { mr, chunks: [] as Blob[], stream, timer: 0, started: Date.now() } as NonNullable<typeof rec.current>;
      mr.ondataavailable = (e) => { if (e.data.size) r.chunks.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(r.timer);
        const secs = Math.max(1, Math.round((Date.now() - r.started) / 1000));
        const mime = (mr.mimeType || type || 'audio/webm').split(';')[0]!;
        const ext = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
        const f = r.chunks.length ? new File(r.chunks, `Voice note ${clock(secs)}.${ext}`, { type: mime }) : null;
        rec.current = null; setState('idle'); setSecs(0);
        r.done?.(f);
      };
      r.timer = window.setInterval(() => { const n = Math.round((Date.now() - r.started) / 1000); setSecs(n); if (n >= MAX_VOICE) stop(); }, 250);
      rec.current = r;
      mr.start(1000);
      setState('rec');
    } catch (e) {
      setError((e as Error)?.name === 'NotAllowedError' ? 'Allow the microphone in your browser to record a voice note.' : 'No microphone was found on this device.');
    }
  }
  function stop(): Promise<File | null> {
    return new Promise((res) => { const r = rec.current; if (!r) return res(null); r.done = res; if (r.mr.state !== 'inactive') r.mr.stop(); });
  }
  async function cancel() { const r = rec.current; if (r) { r.done = () => undefined; r.chunks = []; if (r.mr.state !== 'inactive') r.mr.stop(); } }
  useEffect(() => () => { void cancel(); }, []);
  return { supported, state, secs, error, start, stop, cancel };
}

/** The message list and composer, shared by the client portal and the CRM. */
export function ChatThread({ messages, side, onSend, loadFile, disabled, disabledText, allowInternal, placeholder, typing, onTyping }: {
  messages: ChatMessage[]; side: 'client' | 'staff'; disabled?: boolean; disabledText?: string; allowInternal?: boolean; placeholder?: string;
  /** "John Doe is typing", "Asha is typing"… shown under the messages. */ typing?: string | null; onTyping?: () => void;
  onSend: (text: string, file: File | null, internal: boolean) => Promise<boolean>; loadFile: (id: string) => Promise<string>;
}) {
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const lastTyped = useRef(0);
  const typed = () => { if (onTyping && Date.now() - lastTyped.current > 2500) { lastTyped.current = Date.now(); onTyping(); } };
  const fileIn = useRef<HTMLInputElement>(null);
  const voice = useVoiceNote();
  async function sendVoice() {
    const f = await voice.stop();
    if (!f) return;
    setBusy(true);
    await onSend('', f, internal);
    setBusy(false);
  }
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [messages.length, !!typing]);
  async function send(e?: FormEvent) {
    e?.preventDefault();
    if (busy || (!text.trim() && !file)) return;
    setBusy(true);
    const ok = await onSend(text, file, internal);
    setBusy(false);
    if (ok) { setText(''); setFile(null); if (fileIn.current) fileIn.current.value = ''; }
  }
  return (
    <div className="chat">
      <div className="chat-log" aria-live="polite">
        {messages.map((m) => m.kind === 'system'
          ? <p key={m.id} className="chat-sys">{m.body}<small>{time(m.createdAt)}</small></p>
          : (
            <div key={m.id} className={`chat-msg ${m.kind === side || (side === 'staff' && m.kind === 'staff') ? 'mine' : 'theirs'}${m.internal ? ' note' : ''}${m.kind === 'bot' ? ' bot' : ''}`}>
              <div className="chat-meta">{m.internal && <Lock />}{m.internal ? 'Internal note · ' : ''}{m.kind === 'client' ? (side === 'client' ? 'You' : 'Client') : m.from}<span>{time(m.createdAt)}</span></div>
              {m.body && <p style={m.kind === 'bot' ? { whiteSpace: 'pre-line' } : undefined}>{m.body}</p>}
              {m.file && <Attachment id={m.file.id} name={m.file.name} mime={m.file.mime} load={loadFile} />}
            </div>
          ))}
        {typing && <div className="chat-typing" role="status"><span className="chat-dots" aria-hidden="true"><i /><i /><i /></span>{typing}…</div>}
        <div ref={end} />
      </div>
      {disabled ? <p className="chat-closed">{disabledText ?? 'This chat is closed.'}</p> : (
        <form className={`chat-in${internal ? ' note' : ''}`} onSubmit={send}>
          {allowInternal && <div className="chat-mode" role="group"><button type="button" className={!internal ? 'on' : ''} onClick={() => setInternal(false)}>Reply to client</button><button type="button" className={internal ? 'on' : ''} onClick={() => setInternal(true)}><Lock />Internal note</button></div>}
          {file && <span className="chat-pending"><Paperclip />{file.name}<button type="button" aria-label="Remove file" onClick={() => { setFile(null); if (fileIn.current) fileIn.current.value = ''; }}><X /></button></span>}
          {voice.error && <p className="chat-voice-err" role="alert">{voice.error}</p>}
          {voice.state === 'rec' ? (
            <div className="chat-row chat-recording" role="group" aria-label="Recording a voice note">
              <button type="button" className="chat-attach" aria-label="Discard voice note" onClick={() => void voice.cancel()}><Trash2 /></button>
              <span className="rec-live"><i aria-hidden />Recording {clock(voice.secs)}<small>up to {clock(MAX_VOICE)}</small></span>
              <button type="button" className="chat-send" aria-label="Send voice note" onClick={() => void sendVoice()}><Send /></button>
            </div>
          ) : (
          <div className="chat-row">
            <button type="button" className="chat-attach" aria-label="Attach a file" onClick={() => fileIn.current?.click()}><Paperclip /></button>
            <input ref={fileIn} type="file" hidden accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,video/mp4,audio/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            <textarea rows={1} value={text} placeholder={internal ? 'Only staff see this note' : placeholder ?? 'Write a message'} onChange={(e) => { setText(e.target.value); typed(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }} />
            {!text.trim() && !file && voice.supported
              ? <button type="button" className="chat-send chat-mic" disabled={busy} aria-label="Record a voice note" title="Record a voice note" onClick={() => void voice.start()}><Mic /></button>
              : <button className="chat-send" disabled={busy || (!text.trim() && !file)} aria-label="Send"><Send /></button>}
          </div>
          )}
        </form>
      )}
    </div>
  );
}
